import React from 'react';
import { useSelector } from 'react-redux';
import { Card, Row, Col, Table, ProgressBar, Badge } from 'react-bootstrap';
import { getGreekCurrencyFormat } from '../../../../_helpers/commonFunctions';

const AccountingDebtView = ({ tabKey, debtData }) => {
    const reduxDebt = useSelector((state) => {
        if (!tabKey) return null;
        return state.ACCOUNTING_DATA?.selectedUnregulatedDebts?.[tabKey] ?? null;
    });

    const currentDebt = debtData || reduxDebt;

    if (!currentDebt) {
        return (
            <div className="p-4 text-center text-muted">
                Δεν υπάρχουν διαθέσιμα στοιχεία για τη συγκεκριμένη οφειλή.
            </div>
        );
    }

    const initial = Number(currentDebt.initialAmount || 0);
    const paid = Number(currentDebt.totalPaidAmount || 0);
    const remaining = Number(currentDebt.totalAmount || 0);
    const overdue = Number(currentDebt.overdueAmount || 0);
    const surcharges = Number(currentDebt.surcharges || 0);
    const paidPercent = initial > 0 ? Math.min(100, Math.round((paid / initial) * 100)) : 0;

    return (
        <div className="p-3 w-100" style={{ backgroundColor: '#f8fafc', minHeight: '100%' }}>
            <div className="bg-white p-3 rounded border mb-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-1 font-weight-bold text-dark">{currentDebt.taxType}</h4>
                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            <span>ΑΦΜ: <strong className="text-dark">{currentDebt.companyVatNumber}</strong></span>
                            {currentDebt.paymentCode && (
                                <>
                                    <span className="mx-2">•</span>
                                    <span>Ταυτότητα Οφειλής: <strong className="text-dark">{currentDebt.paymentCode}</strong></span>
                                </>
                            )}
                            <span className="mx-2">•</span>
                            <span>ΔΟΥ: <strong className="text-dark">{currentDebt.doy}</strong></span>
                            <span className="mx-2">•</span>
                            <span>Οικ. Έτος: <strong className="text-dark">{currentDebt.taxYear}</strong></span>
                            <span className="mx-2">•</span>
                            <span>Ημ/νία Βεβαίωσης: <strong className="text-dark">{currentDebt.assessmentDate}</strong></span>
                        </div>
                    </div>
                </div>
            </div>

            <Row className="mb-3">
                <Col md={3}>
                    <Card className="border shadow-none">
                        <Card.Body className="py-2 px-3">
                            <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                Αρχικο Βεβαιωθεν
                            </span>
                            <h4 className="mb-0 mt-1 font-weight-bold text-dark">
                                {getGreekCurrencyFormat(initial)}
                            </h4>
                            <small className="text-muted">Αρχικό ποσό οφειλής</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border shadow-none">
                        <Card.Body className="py-2 px-3">
                            <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                Εξοφληθεν Ποσο
                            </span>
                            <h4 className="mb-0 mt-1 font-weight-bold">
                                {getGreekCurrencyFormat(paid)}
                            </h4>
                            <small className="font-weight-bold">{paidPercent}% αποπληρωμή</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border shadow-none">
                        <Card.Body className="py-2 px-3">
                            <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                Εναπομειναν Υπολοιπο
                            </span>
                            <h4 className="mb-0 mt-1 font-weight-bold text-primary">
                                {getGreekCurrencyFormat(remaining)}
                            </h4>
                            <small className="text-muted">
                                Μη ληξιπρόθεσμο: {getGreekCurrencyFormat(currentDebt.nonOverdueAmount || remaining)}
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border shadow-none">
                        <Card.Body className="py-2 px-3">
                            <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                Ληξιπροθεσμο / Τοκοι
                            </span>
                            <h4 className={`mb-0 mt-1 font-weight-bold ${overdue > 0 ? 'text-danger' : 'text-dark'}`}>
                                {getGreekCurrencyFormat(overdue)}
                            </h4>
                            <small className="text-muted">Προσαυξήσεις: {getGreekCurrencyFormat(surcharges)}</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border mb-3 shadow-none">
                <Card.Body className="py-2 px-3">
                    <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '0.8rem' }}>
                        <span className="text-muted font-weight-bold">Πρόοδος Αποπληρωμής</span>
                        <span className="text-dark font-weight-bold">
                            {getGreekCurrencyFormat(paid)} / {getGreekCurrencyFormat(initial)} ({paidPercent}%)
                        </span>
                    </div>
                    <ProgressBar
                        now={paidPercent}
                        variant={"primary"}
                        style={{ height: '10px' }}
                    />
                </Card.Body>
            </Card>

            <Card className="border mb-3 shadow-none">
                <Card.Header className="bg-white border-bottom py-2 px-3">
                    <span className="font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>
                        Χρονοδιάγραμμα Δόσεων
                    </span>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive className="mb-0 text-center" style={{ tableLayout: 'fixed' }}>
                        <thead className="bg-light border-bottom text-muted" style={{ fontSize: '0.8rem' }}>
                        <tr>
                            <th style={{ width: '6%' }}>Α/Α</th>
                            <th style={{ width: '15%' }}>ΗΜ/ΝΙΑ ΛΗΞΗΣ</th>
                            <th style={{ width: '18%' }}>ΥΠΟΛΟΙΠΟ ΔΟΣΗΣ</th>
                            <th style={{ width: '15%' }}>ΠΡΟΣΑΥΞΗΣΕΙΣ</th>
                            <th style={{ width: '18%' }}>ΣΥΝΟΛΙΚΟ ΠΟΣΟ</th>
                            <th style={{ width: '12%' }}>ΑΝΑΣΤΟΛΗ</th>
                            <th style={{ width: '16%' }}>ΚΑΤΑΣΤΑΣΗ</th>
                        </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.85rem' }}>
                        {currentDebt.installments && currentDebt.installments.length > 0 ? (
                            currentDebt.installments.map((inst, idx) => {
                                const isInstPaid = Number(inst.remainingAmount || 0) === 0;

                                return (
                                    <tr key={idx}>
                                        <td className="font-weight-bold">{inst.installmentNo}</td>
                                        <td>{inst.dueDate}</td>
                                        <td className="font-weight-bold">{getGreekCurrencyFormat(inst.remainingAmount)}</td>
                                        <td>{getGreekCurrencyFormat(inst.surcharges)}</td>
                                        <td className="font-weight-bold">{getGreekCurrencyFormat(inst.totalAmount)}</td>
                                        <td>
                                            {inst.isSuspended ? (
                                                <span className="text-warning font-weight-bold">ΝΑΙ</span>
                                            ) : (
                                                <span className="text-muted">ΟΧΙ</span>
                                            )}
                                        </td>
                                        <td>
                                            {isInstPaid ? (
                                                <Badge bg="primary" className="badge-primary text-white">
                                                    ΕΞΟΦΛΗΜΕΝΗ
                                                </Badge>
                                            ) : (
                                                <Badge bg="warning" className="badge-warning text-dark">
                                                    ΕΚΚΡΕΜΗΣ
                                                </Badge>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-muted py-4">
                                    Δεν υπάρχουν στοιχεία δόσεων.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card className="border mb-3 shadow-none">
                <Card.Header className="bg-white border-bottom py-2 px-3">
                    <span className="font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>
                        Αποδεικτικά Είσπραξης myAADE
                    </span>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive className="mb-0 text-center" style={{ tableLayout: 'fixed' }}>
                        <thead className="bg-light text-muted border-bottom" style={{ fontSize: '0.8rem' }}>
                        <tr>
                            <th style={{ width: '12%' }}>ΗΜ/ΝΙΑ ΠΛΗΡΩΜΗΣ</th>
                            <th style={{ width: '17%' }}>ΚΩΔΙΚΟΣ ΣΥΝΑΛΛΑΓΗΣ (ΔΙΑΣ)</th>
                            <th style={{ width: '10%' }}>ΤΡΟΠΟΣ</th>
                            <th style={{ width: '10%' }}>ΑΤΒ</th>
                            <th style={{ width: '11%' }}>ΗΜ/ΝΙΑ ΑΤΒ</th>
                            <th style={{ width: '11%' }}>Α/Α ΔΙΠΛΟΤΥΠΟΥ</th>
                            <th style={{ width: '15%' }}>ΥΠΗΡΕΣΙΑ ΕΚΔΟΣΗΣ</th>
                            <th style={{ width: '14%' }}>ΠΟΣΟ ΠΛΗΡΩΜΗΣ</th>
                        </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.85rem' }}>
                        {currentDebt.payments && currentDebt.payments.length > 0 ? (
                            currentDebt.payments.map((p, idx) => (
                                <tr key={idx}>
                                    <td className="font-weight-bold">{p.paymentDate}</td>
                                    <td className="text-truncate" title={p.paymentCode}>{p.paymentCode}</td>
                                    <td>{p.paymentType || 'Κάρτα'}</td>
                                    <td>{p.atb || '-'}</td>
                                    <td>{p.atbDate || '-'}</td>
                                    <td>{p.receiptNo || '-'}</td>
                                    <td className="text-truncate" title={p.receiptService || '-'}>{p.receiptService || '-'}</td>
                                    <td className="font-weight-bold text-primary">
                                        {getGreekCurrencyFormat(p.allocatedAmount || p.paymentAmount)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-muted py-4">
                                    Δεν έχουν αντιστοιχιστεί αποδεικτικά πληρωμών για αυτή την οφειλή.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AccountingDebtView;